import type { UserRole } from "../types/auth.types";

/**
 * Extracts the exact role string from the backend login/API response.
 * Handles both string and string[] shapes returned by the API.
 */
export function extractBackendRole(
  userRole: string | string[] | null | undefined
): string | null {
  if (!userRole) {
    return null;
  }

  if (Array.isArray(userRole)) {
    return userRole.length > 0 ? String(userRole[0]).trim() : null;
  }

  const trimmed = String(userRole).trim();
  return trimmed || null;
}

/**
 * Resolves the active role from available auth sources.
 * Priority: stored user role → userDetails API role → JWT claim.
 */
export function resolveUserRole(
  userRole: string | null | undefined,
  detailsRole: string | null | undefined,
  tokenRole: string | null | undefined
): string | null {
  return extractBackendRole(userRole) ?? extractBackendRole(detailsRole) ?? extractBackendRole(tokenRole);
}

/**
 * Checks whether the user's exact backend role is in the allowed list.
 * Comparison is case-sensitive to match backend values exactly.
 */
export function userHasRouteAccess(
  userRole: string | null,
  allowedRoles: UserRole[]
): boolean {
  if (!userRole || allowedRoles.length === 0) {
    return false;
  }

  return allowedRoles.includes(userRole);
}

/**
 * Case-insensitive role comparison helper for UI checks when needed.
 */
export function rolesMatch(roleA: string | null, roleB: string | null): boolean {
  if (!roleA || !roleB) {
    return false;
  }

  return roleA.trim().toLowerCase() === roleB.trim().toLowerCase();
}
